import React, { useMemo } from 'react';
import {
  useElement,
  usePlateEditorRef,
  useEventPlateId,
  getPluginOptions,
  focusEditor,
  findNode,
  setNodes,
  unsetNodes,
  moveChildren,
  removeNodes,
  DropdownMenu,
  useTableStore,
  getTableAbove,
  insertTableColumn,
  insertTableRow,
  insertElements,
  deleteRow,
  deleteColumn,
  toDOMNode,
  ELEMENT_TD,
  ELEMENT_PARAGRAPH,
  getEmptyCellNode,
  ELEMENT_TABLE,
} from '@udecode/plate';
import { useSelected } from 'slate-react';
import { Path } from 'slate';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  offset,
  shift,
  flip,
} from '@floating-ui/react';

import { ElementPopover } from '../../components/popup/Floating/ElementPopover';
import { PopoverButton } from '../../components/button/PopoverButton';
import { RemoveNodeButton } from '../../components/button/RemoveNodeButton';
import { PlateTableBordersMenu } from './PlateTableBordersMenu';
import { PlateTableBackgroundPicker } from './PlateTableBackgroundPicker';
import { getCurrentCellData } from './utils/getCurrentCellData';
import { getMergedCells } from './utils/getMergedCells';

export const PlateTablePopover = ({ children, ...props }) => {
  const element = useElement();
  const selected = useSelected();
  const selectedCells = useTableStore().get.selectedCells();
  const editor = usePlateEditorRef(useEventPlateId());
  const isSelectingMultipleCells = selectedCells && selectedCells.length > 1;

  const {
    currentRowPath,
    currentColPath,
    isSelectingCellsInSameRow,
    isInMergedCell,
  } = getCurrentCellData(editor, selectedCells);

  const _selectedCellPaths = selectedCells ? selectedCells.map(cell => {
    const [node, path] = findNode(editor, { match: { id: cell.id }, at: [] }) ?? [];
    if (!path) return null;
    return { ...node, path };
  }) : [];
  // When merging cells, the selected cells don't update and so we need to filter out the merged cells.
  const selectedCellPaths = _selectedCellPaths.filter(c => !!c);

  const selectedCellsCount = selectedCellPaths.reduce((rows, { path, colSpan } = {}) => {
    if (!path) return rows;
    const rowNumber = path[path.length - 2];
    if (rows[rowNumber]) {
      if (colSpan && colSpan > 1) return { ...rows, [rowNumber]: rows[rowNumber] + colSpan };
      return { ...rows, [rowNumber]: rows[rowNumber] + 1 };
    }
    return { ...rows, [rowNumber]: (colSpan && colSpan > 1) ? colSpan : 1 };
  }, []);

  const domNode = useMemo(() => {
    const { currentCell } = getCurrentCellData(editor, selectedCells);
    if (!selectedCells && !currentCell) return;

    if (!selectedCells) return toDOMNode(editor, currentCell[0]);

    // We will use the last item in the selection to shortcut doing more
    // complex calculating involving a virtual element of everything selected.
    const cell = selectedCells[selectedCells.length -1];
    if (cell) {
      return toDOMNode(editor, cell);
    }
  }, [selectedCells, editor.selection]);

  /**
   * Returns number of rows and columns as a tuple [number of rows, number of columns].
   * Used for preventing deleting multiple rows/cols when all are selected.
   */
  const getRowColCount = () => {
    const rowCount = element?.children?.length ? element.children.length : 0;
    if (rowCount === 0) return [0, 0];
    const colCount = element.children.reduce((acc, child) => {
      if (child.children?.length) {
        if (child.children.length > acc) return child.children.length;
        return acc;
      }
      return acc;
    }, 0);
    return [rowCount, colCount];
  };

  const [rowCount, colCount] = getRowColCount();
  const selectedRowCount = Object.keys(selectedCellsCount).length;
  const selectedColCount = Object.keys(selectedCellsCount).reduce((count, _row) => {
    if (selectedCellsCount[_row] > count) return selectedCellsCount[_row];
    return count;
  }, 0);

  const selectingAllRows = rowCount <= selectedRowCount;
  const selectingAllCols = colCount <= selectedColCount;
  const selectingAllCells = selectingAllRows && selectingAllCols;

  const mount = window.appRootMountPoint || document.body;

  const floatingOptions = {
    middleware: [
      offset(12),
      flip(),
    ],
    ... domNode && {
      elements: {
        reference: domNode,
      },
    },
    ... selected && { open: true },
  };

  // Add shift for long tables if table is active but not selected so that popover always appears.
  if (!domNode) {
    floatingOptions.middleware.push(shift({
      crossAxis: true,
      rootBoundary: 'viewport',
    }));
  };

  const deleteMultipleRows = () => {
    if (!selectedCells) return;
    const pathsToDelete = [];
    selectedCells.forEach((col) => {
      const [, path] = findNode(editor, { match: { id: col.id }, at: [] });
      if (path) {
        const rowIndex = path[path.length - 2];
        if (pathsToDelete.length === 0) {
          pathsToDelete.push(path.slice(0, -1));
        } else {
          const existingRow = pathsToDelete.find(p => {
            // A table selection can hold multiple cells within the same column.
            // We want to make sure we're not trying to delete the same row multiple times.
            // e.g. [0, 1, 1] and [0, 1, 2] are the same column.
            if (p[p.length - 1] === rowIndex) {
              return true;
            }
            return false;
          });
          if (!existingRow) pathsToDelete.push(path.slice(0, -1));
        }
      }
    });
    // Sort from right to left so that the col order doesn't shift.
    pathsToDelete.sort((a, b) => {
      const aCol = a[a.length - 1];
      const bCol = b[b.length - 1];
      return bCol - aCol;
    });
    pathsToDelete.forEach(p => {
      focusEditor(editor, p);
      deleteRow(editor);
    });
  };

  const _deleteColumn = () => {
    // Need to determine if deleting current column will affect merged cells.
    const { currentCell } = getCurrentCellData(editor, selectedCells);
    const [, pathColToDelete] = currentCell;
    if (!pathColToDelete) return;
    const colNum = pathColToDelete[pathColToDelete.length -1];
    const [, tablePath] = getTableAbove(editor);
    const mergedCells = getMergedCells(editor, tablePath);

    mergedCells.forEach(mc => {
      const [mcNode, mcPath] = mc;
      const mcColNum = mcPath[mcPath.length - 1];
      const { colSpan } = mcNode;

      if (mcColNum + colSpan >= colNum && mcColNum <= colNum) {
        if (colSpan > 2) {
          setNodes(editor, { colSpan: colSpan - 1 }, { at: mcPath });
        }
        else {
          // Adding the column number of the merged cell and colSpan of merged cell will tell
          // us the end range of the merged cell. We need to know if the column we're deleting
          // is inside a merged cell.
          unmergeCells(mcPath);
        }
      }
    });

    deleteColumn(editor);
  };

  const deleteMultipleCols = () => {
    if (!selectedCells) return;

    const pathsToDelete = [];
    selectedCells.forEach((col) => {
      const [, path] = findNode(editor, { match: { id: col.id }, at: [] });
      if (path) {
        const colIndex = path[path.length - 1];
        if (pathsToDelete.length === 0) {
          pathsToDelete.push(path);
        } else {
          const existingCol = pathsToDelete.find(p => {
            // A table selection can hold multiple cells within the same column.
            // We want to make sure we're not trying to delete the same column multiple times.
            // e.g. [0, 1, 1] and [0, 2, 1] are the same column.
            if (p[p.length - 1] === colIndex) {
              return true;
            }
            return false;
          });
          if (!existingCol) pathsToDelete.push(path);
        }
        // When a cell has colspan, then the number of children do not match other rows, so if we encounter
        // colspan, we must construct the paths that would be there if the colspan was not present.
        if (col.colSpan) {
          const rowPath = path.slice(0, -1);
          const _colIndex = path[path.length - 1];
          // Construct path for remaining columns that aren't selected due to a merged cell.
          for (let i = 1; i < col.colSpan; i += 1) {
            const newIndex = _colIndex + 1;
            const existingCol = pathsToDelete.find(p => {
              if (p[p.length - 1] === newIndex) {
                return true;
              }
              return false;
            });
            if (!existingCol) pathsToDelete.push([...rowPath, newIndex]);
          }
        }
      }
    });
    // Sort from right to left so that the col order doesn't shift.
    pathsToDelete.sort((a, b) => {
      const aCol = a[a.length - 1];
      const bCol = b[b.length - 1];
      return bCol - aCol;
    });
    pathsToDelete.forEach(p => {
      focusEditor(editor, p);
      _deleteColumn(editor);
    });
  };

  const mergeCells = () => {
    // Need to account for merging cells with existing colspans. Otherwise merging gets messed up.
    const numberOfCellsToMerge = selectedCells.reduce((prev, cell) => cell.colSpan ? prev + cell.colSpan : prev + 1, 0);

    // Keep first path for moving children.
    let firstCellPath;

    // This assumes selected cells are in a row because of the condition for displaying
    // merge button. Logic will be much more complex with different types of cell merges.
    selectedCells.forEach((col, colIdx) => {
      const foundCell = findNode(editor, { match: { id: col.id }, at: [] });
      if (foundCell) {

        if (colIdx === 0) {
          // Set first column in selected cells to span across a number of columns.
          setNodes(
            editor,
            {
              colSpan: numberOfCellsToMerge,
            },
            { at: foundCell[1] },
          );
          firstCellPath = foundCell[1].slice();
        }
        else {
          // Need to manually move children first, then we can delete the cell.
          moveChildren(editor, {
            at: foundCell[1],
            to: [...firstCellPath, colIdx],
          });
          removeNodes(
            editor,
            { at: foundCell[1] },
          );
        }
      }
    });

    // Combine paragraph elements so they don't create new lines when merging cells.
    const [mergedCellNode, mergedCellPath] = findNode(
      editor,
      {
        at: firstCellPath,
        match: (n) => n.type === ELEMENT_TD,
      });

    if (mergedCellNode?.children) {
      // Keep record of prev node type so we know what index to use when merging children.
      let prevNodeType = '';
      let newChildIdx = 0;

      mergedCellNode.children.forEach((child, childIdx) => {
        const foundNodeEntry = findNode(editor, { match: { id: child.id }, at: [] });
        // This could be generalized for any kind of nodes, but only caring about paragraphs for now,
        // as merging other same node type's children might be more complicated than simply moving children.
        if (prevNodeType === child.type && foundNodeEntry?.length > 1 && child.type === ELEMENT_PARAGRAPH) {
          // Find the child we're moving other children into so we know at what index to move other children to.
          // Otherwise we could be merging 5 <p> elements, but marks act as spans, so the ordering gets messed up.
          // e.g. [{type: p, 1}, {type: p, 2}, {type: p, bold, 3}, {type: p, 4}, {type: p, 5}] would become
          // [{type: p, 1245}, {type: p, bold, 3}]. We want [{type: p, 12}, {type: p, bold, 3}, {type: p, 45}].
          const [childToUpdate] = findNode(
            editor,
            {
              at: mergedCellPath.concat(newChildIdx),
              match: (n) => n.type === child.type,
            },
          );
          const concatIdx = childToUpdate.children.length;
          moveChildren(editor, {
            at: foundNodeEntry[1],
            to: mergedCellPath.concat(newChildIdx).concat(concatIdx),
          });
          removeNodes(
            editor,
            { at: foundNodeEntry[1] },
          );
        } else if (childIdx !== 0) {
          newChildIdx += 1;
        }
        prevNodeType = child.type;
      });
    }
  };

  const unmergeCells = (at) => {
    // Being careful about crashing the editor.
    const {
      currentCell,
      colSpan,
    } = getCurrentCellData(editor, selectedCells, at);

    if (colSpan < 2 || !currentCell) return;

    // Insert elements at the current editor selection will add cells to the left. Need to add to the right.
    const cellsToAdd = colSpan - 1;
    const nextPath = Path.next(currentCell[1]);

    const { newCellChildren } = getPluginOptions(editor, ELEMENT_TABLE);
    unsetNodes(editor, 'colSpan', { at: currentCell[1] });

    // Remove attribute if has colspan attribute from pasted table.
    if (currentCell[0]?.attributes?.colspan) {
      const newAttributes = currentCell[0].attributes;
      delete newAttributes.colspan;
      // Generally, deleting the colspan attribute will just result in empty object, so just remove from node.
      if (!newAttributes) unsetNodes(editor, 'attributes', { at: currentCell[1] });
      else setNodes(editor, { attributes: newAttributes }, { at: currentCell[1] });
    }
    for (let i = 0; i < cellsToAdd; i += 1) {
      insertElements(
        editor,
        getEmptyCellNode(editor, {
          header: false,
          newCellChildren,
        }),
        {
          at: nextPath,
        },
      );
    }
  };

  return (
    <ElementPopover
      content={(
        <div>
          {isInMergedCell && (
            <button
              className="button--popover button--unstyled"
              onClick={() => unmergeCells()}
            >
              Unmerge Cell
            </button>
          )}
          {isSelectingCellsInSameRow && (
            <button
              className="button--popover button--unstyled"
              onClick={mergeCells}
            >
              Merge Cells
            </button>
          )}
          <DropdownMenu.Root
            modal={false}
          >
            <DropdownMenu.Trigger asChild>
              <PopoverButton
                type="button"
                aria-label="Borders"
                className="dropdown-button--right"
              >
                <div>Borders</div>
                <FontAwesomeIcon
                  icon={['fal', 'chevron-right']}
                  size="xs"
                />
              </PopoverButton>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal
              container={mount}
            >
              <PlateTableBordersMenu />
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          <DropdownMenu.Root
            modal={false}
          >
            <DropdownMenu.Trigger asChild>
              <PopoverButton
                type="button"
                aria-label="background"
                className="dropdown-button--right"
              >
                Cell Background
                <FontAwesomeIcon
                  icon={['fal', 'chevron-right']}
                  size="xs"
                />
              </PopoverButton>
            </DropdownMenu.Trigger>

            <DropdownMenu.Portal
              container={mount}
            >
              <PlateTableBackgroundPicker />
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
          {!isSelectingMultipleCells && (
            <>
              <DropdownMenu.Root modal={false}>
                <DropdownMenu.Trigger asChild>
                  <PopoverButton
                    type="button"
                    aria-label="insert"
                    className="dropdown-button--right"
                  >
                    Insert
                    <FontAwesomeIcon
                      icon={['fal', 'chevron-right']}
                      size="xs"
                    />
                  </PopoverButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal
                  container={mount}
                >
                  <DropdownMenu.Content
                    side="right"
                    align="start"
                    sideOffset={8}
                    className="slate-popover slate-popover--table"
                    style={{ zIndex: 40 }}
                  >
                    <PopoverButton
                      role="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        insertTableRow(editor, { at: currentRowPath });
                        focusEditor(editor);
                      }}
                    >
                      Insert row above
                    </PopoverButton>
                    <PopoverButton
                      role="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        insertTableRow(editor);
                        focusEditor(editor);
                      }}
                    >
                      Insert row below
                    </PopoverButton>
                    <PopoverButton
                      role="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        insertTableColumn(editor, { at: currentColPath });
                        focusEditor(editor);
                      }}
                    >
                      Insert column left
                    </PopoverButton>
                    <PopoverButton
                      role="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        insertTableColumn(editor);
                        focusEditor(editor);
                      }}
                    >
                      Insert column right
                    </PopoverButton>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <PopoverButton
                    role="button"
                    aria-label="Delete"
                    className="dropdown-button--right"
                  >
                    Delete
                    <FontAwesomeIcon
                      icon={['fal', 'chevron-right']}
                      size="xs"
                    />
                  </PopoverButton>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    side="right"
                    align="start"
                    sideOffset={8}
                    className="slate-popover slate-popover--table"
                    style={{ zIndex: 40 }}
                  >
                    <PopoverButton
                      role="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        deleteRow(editor);
                      }}
                    >
                      Delete Row
                    </PopoverButton>
                    <PopoverButton
                      role="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        _deleteColumn();
                      }}
                    >
                      Delete Column
                    </PopoverButton>
                    <RemoveNodeButton
                      element={element}
                      contentEditable={false}
                      showIcon={false}
                    >
                      <div>Delete Table</div>
                    </RemoveNodeButton>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </>
          )}
          {(isSelectingMultipleCells && !selectingAllCells) && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <PopoverButton
                  role="button"
                  aria-label="Delete"
                  className="dropdown-button--right"
                >
                  Delete
                  <FontAwesomeIcon
                    icon={['fal', 'chevron-right']}
                    size="xs"
                  />
                </PopoverButton>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  side="right"
                  align="start"
                  sideOffset={8}
                  className="slate-popover slate-popover--table"
                  style={{ zIndex: 40 }}
                >
                  {!selectingAllRows && (
                    <PopoverButton
                      role="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        deleteMultipleRows(editor);
                      }}
                    >
                      Delete Rows
                    </PopoverButton>
                  )}
                  {!selectingAllCols && (
                    <PopoverButton
                      role="button"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();

                        deleteMultipleCols(editor);
                      }}
                    >
                      Delete Columns
                    </PopoverButton>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </div>
      )}
      className="slate-popover slate-popover--table"
      floatingOptions={floatingOptions}
      {...props}
    >
      {children}
    </ElementPopover>
  );
};
